import selectors from 'state/selectors';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActionType,
  CallApiAction,
  DocumentFields,
  DocumentHeaderTriggerConfig,
  FieldTriggerConfig,
  RemoveFieldAction,
} from 'models/response';
import { actionType, ACTION_TRIGGER_CONFIG } from 'utils/constants';
import { useFormikContext } from 'formik';
import { FormValues, Q1Value } from 'models/documents';
import actions from 'state/actions';

export type HEADERKEYS = 'supplierCode';
export type ACTIONID = 'wDocBatch/rebatch';

function isCallApiAction(action: ActionType): action is CallApiAction {
  return action.action === ACTION_TRIGGER_CONFIG.callApi;
}

function isRemoveFieldAction(action: ActionType): action is RemoveFieldAction {
  return action.action === ACTION_TRIGGER_CONFIG.removeField;
}

const useGetToolbarAction = () => {
  const dispatch = useDispatch();

  return (action: CallApiAction) => {
    switch (action.endpoint) {
      case 'wDocBatch/rebatch':
        return () => {
          dispatch(actions.modal.openModal(actionType.RETURN_TO_BATCH));
        };
      default:
        return undefined;
    }
  };
};

export const useExecuteToolbarActionDocHeader = () => {
  const triggers = useSelector(selectors.documents.selectDocumentHeader)?.triggerConfig;
  const getAction = useGetToolbarAction();

  return async (key: HEADERKEYS) => {
    if (!triggers) return;
    const trigger = triggers.find((trigger) => trigger.key === key);
    if (!trigger) return;
    for (const action of trigger.actions) {
      if (isCallApiAction(action)) {
        await getAction(action as CallApiAction)?.();
      }
    }
  };
};

export const useExecuteActionQ1 = (triggers: FieldTriggerConfig | null) => {
  const documentFields = useSelector(selectors.documents.selectDocumentFields);
  const { setFieldValue, values } = useFormikContext<Record<string, Q1Value>>();
  return () => {
    if (!triggers) return;
    for (const action of triggers.actions) {
      if (isRemoveFieldAction(action)) {
        const allFields = documentFields?.flatMap((section) => section.sectionFields);
        const foundField = allFields?.find((field) => field.idField === action.target);

        if (foundField) {
          setFieldValue(
            `${foundField.fieldName}.content`,
            values[foundField.fieldName]?.content?.map((item) => ({
              ...item,
              content: '',
            })) || [],
          );
        }
      }
    }
  };
};

export const applyRemoveField = (
  triggers: DocumentHeaderTriggerConfig[],
  key: HEADERKEYS,
  values: FormValues,
  documentFields: DocumentFields | null,
): FormValues => {
  const trigger = triggers?.find((t) => t.key === key);
  if (!trigger) return values;

  const allFields = documentFields?.flatMap((section) => section.sectionFields);

  let updatedValues = { ...values };

  for (const action of trigger.actions) {
    if (isRemoveFieldAction(action)) {
      const foundField = allFields?.find((field) => field.idField === action.target);

      if (foundField) {
        const fieldName = foundField.fieldName;

        const fieldValue = updatedValues[fieldName] as Q1Value | undefined;

        updatedValues = {
          ...updatedValues,
          [fieldName]: {
            ...fieldValue,
            content:
              fieldValue?.content?.map((item) => ({
                ...item,
                content: '',
              })) || [],
          } as Q1Value,
        };
      }
    }
  }

  return updatedValues;
};
